import type { Reference } from "./models.js";
import type { LineRecord } from "./rows/records.js";
import { chars, count, isLetter, isNumber, splitNewlines, splitSpaces, toInt, trimWS } from "./text.js";

// WeightsCore's `TrainingWeek`, `TrainingPlanText`, `PlannedWeightsSession` and
// `PlannedTrainingDay`: a coach's week as printed, and what one set of its columns trains.

export interface TrainingDay {
  /** Start of day, epoch milliseconds. */
  date: number | null;
  label: string;
  location: string | null;
  /** Group name → the labels printed in that cell, top to bottom. */
  sessions: Map<string, string[]>;
}

export interface TrainingWeek {
  title: string | null;
  groups: string[];
  days: TrainingDay[];
  /** Label → its lines, in the order the labels were defined. */
  glossary: Map<string, string[]>;
  /** Label → per line, what the coach set its exercises to log as (DESIGN.md §25.6); absent, nothing. */
  records?: Map<string, (LineRecord | null)[]>;
}

export interface WeightsSession {
  id: string;
  label: string;
  date: number | null;
  text: string;
  includedPrep: string[];
  /** The definition's own lines, as the glossary holds them. */
  ownLines: string[];
  /** Per line of `text`: the own line it is, the own line that named the definition it came from, or neither. */
  origins: LineOrigin[];
  /** Per line of `text`: what the coach set its exercises to log as, from the definition the line is of. */
  records: (LineRecord | null)[];
}

export type LineOrigin = { own: number } | { via: number } | null;

export interface PlannedDay {
  id: string;
  date: number | null;
  label: string;
  location: string | null;
  weights: WeightsSession[];
  others: Reference[];
  positions: Map<string, number>;
}

// MARK: TrainingPlanText

/** Lowercased, without diacritics, punctuation turned into single spaces. */
export function foldText(text: string): string {
  const folded = text.normalize("NFD").replace(/\p{Mn}/gu, "").normalize("NFC").toLowerCase();
  let out = "";
  let lastWasSpace = true;
  for (const c of folded) {
    if (isLetter(c) || isNumber(c)) {
      out += c;
      lastWasSpace = false;
    } else if (!lastWasSpace) {
      out += " ";
      lastWasSpace = true;
    }
  }
  return trimWS(out);
}

export function words(text: string): string[] {
  return splitSpaces(foldText(text));
}

function trailingDigits(token: string): string {
  const list = chars(token);
  let i = list.length;
  while (i > 0 && isNumber(list[i - 1])) i -= 1;
  return list.slice(i).join("");
}

function trailingNumber(token: string): number | null {
  const digits = trailingDigits(token);
  const n = count(digits);
  if (n === 0 || n > 2) return null;
  if (count(token) - n > 2) return null;
  return toInt(digits);
}

function splitTrailingNumber(token: string): [string, number] | null {
  const digits = trailingDigits(token);
  const n = count(digits);
  if (n === 0 || n > 2) return null;
  const head = chars(token).slice(0, count(token) - n).join("");
  if (count(head) < 3) return null;
  const value = toInt(digits);
  return value === null ? null : [head, value];
}

/** The identity of a session label: `Weights #2`, `Weights N2` and `Weights2` share a key. */
export function labelKey(text: string): string {
  const parts = words(text);
  if (parts.length === 0) return "";
  const lastPart = parts[parts.length - 1]!;
  const number = parts.length > 1 ? trailingNumber(lastPart) : null;
  if (number !== null) {
    parts[parts.length - 1] = `#${number}`;
  } else {
    const split = splitTrailingNumber(lastPart);
    if (split !== null) {
      parts[parts.length - 1] = split[0];
      parts.push(`#${split[1]}`);
    }
  }
  return parts.join(" ");
}

/** The label as the coach wrote it: a photo's `Weights N2` is shown as `Weights #2`. */
export function displayLabel(text: string): string {
  const parts = splitSpaces(text);
  if (parts.length <= 1) return text;
  const lastPart = parts[parts.length - 1]!;
  const digits = count(trailingDigits(lastPart));
  const head = chars(lastPart).slice(0, count(lastPart) - digits);
  const number = trailingNumber(lastPart);
  if (digits === 0 || head.length === 0 || !head.every(isLetter) || number === null) return text;
  parts[parts.length - 1] = `#${number}`;
  return parts.join(" ");
}

/** Only something shaped like `Weights #2` is a glossary reference. */
export function looksLikeLabel(text: string): boolean {
  const key = labelKey(text);
  const lastPart = splitSpaces(key).pop();
  if (lastPart === undefined || !lastPart.startsWith("#")) return false;
  return chars(key).some(isLetter);
}

const everyone = new Set(["tous", "tout", "toutes", "all", "everyone", "everybody", "group", "groupe"]);

export function isEveryone(group: string): boolean {
  return everyone.has(foldText(group));
}

export function isPrepLabel(label: string): boolean {
  const folded = foldText(label);
  return folded.includes("prep") || folded.includes("warm up") || folded.includes("warmup");
}

export function matchesGroup(group: string, name: string): boolean {
  const groupWords = words(group);
  const nameWords = words(name);
  if (groupWords.length === 0 || nameWords.length === 0) return false;
  if (sameWords(groupWords, nameWords)) return true;
  if (containsRun(groupWords, nameWords) || containsRun(nameWords, groupWords)) return true;
  const groupText = groupWords.join("");
  const nameText = nameWords.join("");
  if (Math.min(count(groupText), count(nameText)) < 3) return false;
  return groupText.includes(nameText) || nameText.includes(groupText);
}

function sameWords(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((w, i) => w === b[i]);
}

function containsRun(haystack: string[], needle: string[]): boolean {
  if (needle.length > haystack.length) return false;
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    if (sameWords(haystack.slice(start, start + needle.length), needle)) return true;
  }
  return false;
}

// MARK: PlannedWeightsSession.identifier

const mask = (1n << 64n) - 1n;
const prime = 0x100000001b3n;

/** FNV-1a both ways over the seed's UTF-8, shaped as a version 4 UUID. */
export function identifier(seed: string): string {
  const bytes = utf8(seed);
  let hash = 0xcbf29ce484222325n;
  for (const byte of bytes) hash = ((hash ^ BigInt(byte)) * prime) & mask;
  let tail = hash ^ 0x9e3779b97f4a7c15n;
  for (let i = bytes.length - 1; i >= 0; i -= 1) tail = ((tail ^ BigInt(bytes[i]!)) * prime) & mask;
  const out: number[] = [];
  for (let shift = 56n; shift >= 0n; shift -= 8n) out.push(Number((hash >> shift) & 0xffn));
  for (let shift = 56n; shift >= 0n; shift -= 8n) out.push(Number((tail >> shift) & 0xffn));
  out[6] = (out[6]! & 0x0f) | 0x40;
  out[8] = (out[8]! & 0x3f) | 0x80;
  const hex = out.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** `TrainingWeek.Day.seed`: the date when the label named one, else the label. */
export function daySeed(day: TrainingDay): string {
  return day.date !== null ? String(swiftRounded(day.date / 1000)) : `row|${day.label}`;
}

function swiftRounded(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

export function identifierOn(label: string, day: TrainingDay): string {
  return identifier(`${label}|${daySeed(day)}`);
}

function utf8(text: string): number[] {
  const out: number[] = [];
  for (const c of text) {
    const code = c.codePointAt(0)!;
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    else if (code < 0x10000) out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    else out.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
  }
  return out;
}

// MARK: TrainingWeek


type GlossaryIndex = Map<string, { label: string; lines: string[]; records: (LineRecord | null)[] }>;

export function glossaryIndex(week: TrainingWeek): GlossaryIndex {
  const index: GlossaryIndex = new Map();
  for (const [label, lines] of week.glossary) {
    const key = labelKey(label);
    if (key === "") continue;
    const given = week.records?.get(label);
    const records = lines.map((_, i) => given?.[i] ?? null);
    const existing = index.get(key);
    if (existing !== undefined) index.set(key, { label: existing.label, lines: [...existing.lines, ...lines], records: [...existing.records, ...records] });
    else index.set(key, { label, lines, records });
  }
  return index;
}

function referencedLabels(index: GlossaryIndex): Set<string> {
  const referenced = new Set<string>();
  for (const [key, entry] of index) {
    for (const line of entry.lines) {
      const target = labelKey(line);
      if (target !== key && index.has(target)) referenced.add(target);
    }
  }
  return referenced;
}

function resolveLines(lines: string[], index: GlossaryIndex, visited: Set<string>): string[] {
  return lines.flatMap((line) => {
    const target = labelKey(line);
    const referenced = index.get(target);
    if (visited.has(target) || referenced === undefined) return [line];
    return referenced.lines;
  });
}

/** `resolveLines` on an entry, each line with its record. */
function resolveEntry(entry: { lines: string[]; records: (LineRecord | null)[] }, index: GlossaryIndex, visited: Set<string>): [string, LineRecord | null][] {
  return entry.lines.flatMap((line, i): [string, LineRecord | null][] => {
    const target = labelKey(line);
    const referenced = index.get(target);
    if (visited.has(target) || referenced === undefined) return [[line, entry.records[i] ?? null]];
    return referenced.lines.map((l, j) => [l, referenced.records[j] ?? null]);
  });
}

interface Expanded {
  text: string;
  prep: string[];
  ownLines: string[];
  origins: LineOrigin[];
  records: (LineRecord | null)[];
}

function expand(key: string, index: GlossaryIndex): Expanded {
  const entry = index.get(key);
  if (entry === undefined) return { text: "", prep: [], ownLines: [], origins: [null], records: [null] };
  const prepLabels: string[] = [];
  const prepLines: [string, LineOrigin, LineRecord | null][] = [];
  const body: [string, LineOrigin, LineRecord | null][] = [];
  entry.lines.forEach((line, own) => {
    const target = labelKey(line);
    const referenced = index.get(target);
    if (target === key || referenced === undefined) {
      body.push([line, { own }, entry.records[own] ?? null]);
      return;
    }
    const lines = resolveEntry(referenced, index, new Set([key, target])).map(
      ([l, record]): [string, LineOrigin, LineRecord | null] => [l, { via: own }, record],
    );
    if (isPrepLabel(referenced.label)) {
      prepLabels.push(referenced.label);
      prepLines.push(...lines);
    } else {
      body.push(...lines);
    }
  });
  const all: [string, LineOrigin, LineRecord | null][] =
    prepLines.length === 0 ? body : [["Prep:", null, null], ...prepLines, ["", null, null], ...body];
  return {
    text: all.map(([line]) => line).join("\n"),
    prep: prepLines.length === 0 ? [] : prepLabels,
    ownLines: entry.lines.slice(),
    origins: all.length === 0 ? [null] : all.flatMap(([line, origin]) => splitNewlines(line).map(() => origin)),
    records: all.length === 0 ? [null] : all.flatMap(([line, , record]) => splitNewlines(line).map(() => record)),
  };
}

export function referenceLines(key: string, index: GlossaryIndex): string[] {
  const entry = index.get(key);
  if (entry === undefined) return [];
  return resolveLines(entry.lines, index, new Set([key]));
}


class Reading {
  readonly columns: string[];
  readonly prefixes: string[];
  readonly index: GlossaryIndex;
  readonly referenced: Set<string>;

  constructor(
    readonly week: TrainingWeek,
    groups: readonly string[],
    labelPrefixes: readonly string[],
  ) {
    const wanted = new Set(groups);
    this.columns = week.groups.filter((group) => wanted.has(group));
    this.prefixes = labelPrefixes.map(foldText).filter((prefix) => prefix !== "");
    this.index = glossaryIndex(week);
    this.referenced = referencedLabels(this.index);
  }

  weights(day: TrainingDay): WeightsSession[] {
    const seen = new Set<string>();
    const sessions: WeightsSession[] = [];
    for (const group of this.columns) {
      for (const label of day.sessions.get(group) ?? []) {
        const key = labelKey(label);
        if (seen.has(key)) continue;
        const folded = foldText(label);
        if (!this.prefixes.some((prefix) => folded.startsWith(prefix))) continue;
        if (isPrepLabel(label) || this.referenced.has(key)) continue;
        seen.add(key);
        const expanded = expand(key, this.index);
        const shown = displayLabel(label);
        sessions.push({
          id: identifierOn(shown, day),
          label: shown,
          date: day.date,
          text: expanded.text,
          includedPrep: expanded.prep,
          ownLines: expanded.ownLines,
          origins: expanded.origins,
          records: expanded.records,
        });
      }
    }
    return sessions;
  }

  positions(day: TrainingDay): Map<string, number> {
    const positions = new Map<string, number>();
    for (const group of this.columns) {
      for (const cell of day.sessions.get(group) ?? []) {
        const key = labelKey(cell);
        if (!positions.has(key)) positions.set(key, positions.size);
      }
    }
    return positions;
  }

  others(day: TrainingDay, weights: WeightsSession[]): Reference[] {
    const taken = new Set(weights.map((w) => labelKey(w.label)));
    for (const prep of weights.flatMap((w) => w.includedPrep)) taken.add(labelKey(prep));
    const seen = new Set<string>();
    const others: Reference[] = [];
    for (const group of this.columns) {
      for (const cell of day.sessions.get(group) ?? []) {
        if (!chars(cell).some((c) => isLetter(c) || isNumber(c))) continue;
        const key = labelKey(cell);
        if (taken.has(key) || seen.has(key)) continue;
        seen.add(key);
        const shown = displayLabel(cell);
        others.push({
          id: identifierOn(`ref|${shown}`, day),
          label: shown,
          lines: referenceLines(key, this.index),
          isBeforeLifts: false,
        });
      }
    }
    return others;
  }
}

export function weightsSessions(week: TrainingWeek, groups: readonly string[], labelPrefixes: readonly string[] = ["Weights"]): WeightsSession[] {
  const reading = new Reading(week, groups, labelPrefixes);
  return week.days.flatMap((day) => reading.weights(day));
}

export function trainingDays(week: TrainingWeek, groups: readonly string[], labelPrefixes: readonly string[] = ["Weights"]): PlannedDay[] {
  const reading = new Reading(week, groups, labelPrefixes);
  const days: PlannedDay[] = [];
  for (const day of week.days) {
    const weights = reading.weights(day);
    const others = reading.others(day, weights);
    if (weights.length === 0 && others.length === 0) continue;
    days.push({
      id: identifierOn("day", day),
      date: day.date,
      label: day.label,
      location: day.location,
      weights,
      others,
      positions: reading.positions(day),
    });
  }
  return days;
}

/** `PlannedTrainingDay.others(around:)`: each knows whether the coach put it before the lifts. */
export function othersAround(day: PlannedDay, weights: WeightsSession): Reference[] {
  const lifts = day.positions.get(labelKey(weights.label)) ?? Number.MAX_SAFE_INTEGER;
  return day.others.map((other) => ({
    ...other,
    lines: other.lines.slice(),
    isBeforeLifts: (day.positions.get(labelKey(other.label)) ?? Number.MAX_SAFE_INTEGER) < lifts,
  }));
}
